import { Avatar, Button, Input, List, message, Spin, Tooltip } from 'antd';
import { SyncOutlined, SearchOutlined, RobotOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { findIconDefinition, IconLookup } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Client } from '../../../../types/src';
import { parseCardsList, isPaidClient } from '../../utils';
import { SidebarCard } from '../sidebar/SidebarCard';
import { DeepResearchModal } from './DeepResearchModal';
import { SalesAssistantModal } from './SalesAssistantModal';
import { MarkdownViewerModal } from './MarkdownViewerModal';
import { ResearchHistoryList } from './ResearchHistoryList';

import './inboxSidebarContent.css';
import { DeepResearchState } from '../../../../../redux/deepResearch/deepResearchReducers';
import { SaveCompanyNameModal } from './SaveCompanyNameModal';


interface IInboxSidebarContentProps {
  openReceiverInConnections: any;
  user: any;
  conversation: any;
  updateReceiverNote: any;
  tagsComponent: React.ReactNode;
  selectCampaignComponent: React.ReactNode;
  client: Client;
  updateInbox: () => void;
  isClientOffline: boolean;
  inboxUpdating?: boolean;
  conversationsDetailLoading: boolean;
  deepResearch: DeepResearchState;
  getDeepResearch: (prospect_id: number, page: number, pageSize: number) => void;
  startResearch: (seller_company_name: string, prospect_company_name: string, prospect_id: number, user_guide: string, research_level: string,) => void;
  chat: (query: string, user_id: number, receiver_id: number, campaign_id: number) => void;
  getChatMessages: (campaign_id: number, user_id: number, receiver_id: number, page: number, pageSize: number) => void;
  clearChatError: () => void;
  clearChatHistory: () => void;
  clearResearchHistory: () => void;
  checkOngoingResearchStatus: (prospect_id: number) => void;
  setOngoingResearchStatus: (status: boolean) => void;
  syncResearchStatus: () => void;
  updateClient: (attr: any) => void;
  exportDeepResearchDocument: (id: string, type: string, fileName: string) => void;
  isAdmin?: boolean;
}

export const InboxSidebarContent = ({
  openReceiverInConnections,
  user,
  conversation,
  updateReceiverNote,
  client,
  updateClient,
  updateInbox,
  isClientOffline,
  tagsComponent,
  selectCampaignComponent,
  conversationsDetailLoading,
  deepResearch,
  startResearch,
  getDeepResearch,
  chat,
  getChatMessages,
  clearChatError,
  clearChatHistory,
  isAdmin,
  checkOngoingResearchStatus,
  setOngoingResearchStatus,
  clearResearchHistory,
  syncResearchStatus,
  exportDeepResearchDocument,
}: IInboxSidebarContentProps) => {
  const [inputNoteValue, setInputNoteValue] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [deepResearchModalVisible, setDeepResearchModalVisible] = useState(false);
  const [saveCompanyNameModalVisible, setSaveCompanyNameModalVisible] = useState(false);
  const [salesAssistantModalVisible, setSalesAssistantModalVisible] = useState(false);
  const [markdownModalVisible, setMarkdownModalVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{ id: string, fileUrl: string }>();

  const cards = parseCardsList(conversation, openReceiverInConnections);

  useEffect(() => {
    if (conversation?.receiver?.id) {
      getDeepResearch(conversation?.receiver?.id, 1, 4);
    }

    return () => {
      clearResearchHistory();
    };
  }, [conversation?.receiver?.id, getDeepResearch, clearResearchHistory]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      syncResearchStatus();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [syncResearchStatus]);

  useEffect(() => {
    if (deepResearch.startResearchError) {
      message.error(deepResearch.startResearchError);
    }
  }, [deepResearch.startResearchError]);

  useEffect(() => {
    if (deepResearch.chatError) {
      message.error(deepResearch.chatError);
    }
  }, [deepResearch.chatError]);


  useEffect(() => {
    setInputNoteValue(conversation?.receiver?.inbox_note);
  }, [conversation?.receiver?.inbox_note]);

  const handleUpdateInbox = () => {
    setLoading(true);
    updateInbox();
    setTimeout(() => {
      setLoading(false);
    }, 5000);
  };

  const handleDeepResearch = (config: any) => {
    const seller_company_name = client?.company_name;
    if (!seller_company_name) {
      message.error('Seller company name is required');
      return;
    }
    const prospect_company_name = conversation?.receiver?.company;
    if (!prospect_company_name) {
      message.error('Prospect company name is required');
      return;
    }

    const prospect_id = conversation?.receiver?.id;
    if (!prospect_id) {
      message.error('Prospect ID is required');
      return;
    }
    const user_guide = config.focusAreas.join(', ');
    startResearch(seller_company_name, prospect_company_name, prospect_id, user_guide, config.depth);
    setOngoingResearchStatus(true)
    setDeepResearchModalVisible(false);
  };

  const handleSalesAssistantMessage = (userMessage: string) => {
    if (!user?.id || !conversation?.receiver?.id || !conversation?.campaign_id) {
      message.error('Missing user or receiver information');
      return;
    }

    chat(userMessage, user.id, conversation.receiver.id, conversation.campaign_id);
  };

  const getEmailFromConversation = () => {
    return conversation?.receiver?.email || 'Unknown';
  };

  const getContactNameFromConversation = () => {
    const name = conversation?.receiver?.first_name && conversation?.receiver?.last_name && `${conversation?.receiver?.first_name} ${conversation?.receiver?.last_name}`
    return name || conversation?.receiver?.email || 'Unknown Contact';
  };

  const handleResearchDownload = (params: { id: string, fileUrl: string }) => {
    setSelectedFile(params);
    setMarkdownModalVisible(true);
  };

  const handleDeepResearchModal = useCallback(() => {
    if (!client.company_name?.trim()) {
      setSaveCompanyNameModalVisible(true);
    } else {
      setDeepResearchModalVisible(true);
    }
  }, [client.company_name]);

  if (!conversation) return null;

  if (conversationsDetailLoading) {
    return (
      <div className="inbox__sidebar-spinner-wrapper">
        <Spin size="large" />
      </div>
    );
  }

  const getListItemTitle = (value, link, extra, crm) => {
    const valueSpan = <span className={'inbox__sidebar-title'}>{value || 'Unknown'}</span>;
    if (link) {
      if (crm) {
        return (
          <a style={{ textDecoration: 'underline' }} href={link} target="_blank" rel="noreferrer">
            {value}
          </a>
        );
      }
      return (
        <Link style={{ textDecoration: 'underline' }} to={link}>
          {value}
        </Link>
      );
    }
    if (extra) {
      return (
        <>
          {valueSpan}
          <Tooltip title={extra}>
            <Avatar className={'inbox__sidebar-title-avatar'} size="small">
              +1
            </Avatar>
          </Tooltip>
        </>
      );
    }
    return valueSpan;
  };

  return (
    <div className="inbox__sidebar">
      <div className="inbox__sidebar-row-title" style={{ minHeight: '49px' }}>
        <span style={{ display: 'block', padding: '12px 16px', fontWeight: 'bold' }}>Conversation details</span>
        <Button
          onClick={handleUpdateInbox}
          disabled={!client.enabled || loading || isClientOffline}
          style={{ marginRight: 15 }}
        >
          <SyncOutlined spin={loading} />
        </Button>
      </div>

      <ul style={{ padding: '2px' }}>
        <SidebarCard bordered={false} title={null}>
          <List itemLayout={'horizontal'} style={{ padding: '0' }}>
            {cards?.map(({ key, title, value, link, icon: { prefix, iconName }, extra, crm, disabled }: any) => {
              return (
                value &&
                !disabled && (
                  <List.Item key={key}>
                    <Tooltip placement="left" title={title}>
                      <List.Item.Meta
                        style={{ margin: '0', padding: '0 10px', alignItems: 'center' }}
                        avatar={
                          <Avatar
                            style={{ backgroundColor: 'unset', color: 'unset' }}
                            icon={
                              <FontAwesomeIcon
                                icon={findIconDefinition({
                                  prefix,
                                  iconName,
                                } as IconLookup)}
                                style={{ marginRight: '6px' }}
                              />
                            }
                          />
                        }
                        title={getListItemTitle(value, link, extra, crm)}
                      />
                    </Tooltip>
                  </List.Item>
                )
              );
            })}
          </List>
        </SidebarCard>

        {/* Action Buttons Section */}
        {isPaidClient(client) && (
          <SidebarCard bordered={false} title={null}>
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Button
                loading={deepResearch.startResearchLoading}
                disabled={deepResearch.startResearchLoading}
                type="primary"
                icon={<SearchOutlined />}
                style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                block
                onClick={handleDeepResearchModal}
              >
                Run Deep Research
              </Button>
              <Button
                type="primary"
                icon={<RobotOutlined />}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                block
                onClick={() => setSalesAssistantModalVisible(true)}
              >
                AI Assistant
              </Button>

              <ResearchHistoryList
                researchHistoryMetaData={deepResearch.researchHistoryMetaData}
                researchHistory={deepResearch.researchHistory}
                loading={deepResearch.getResearchLoading}
                getResearch={getDeepResearch}
                onDownloadClick={handleResearchDownload}
                prospectId={conversation?.receiver?.id || 0}
              />
            </div>
          </SidebarCard>
        )}
        <SidebarCard bordered={false} title={null} key={'note'}>
          <div className="conversation__note">
            <Input.TextArea
              style={{ height: '100px' }}
              value={inputNoteValue}
              defaultValue={conversation?.receiver?.inbox_note}
              className="conversation__note-input"
              placeholder="Insert a note..."
              onChange={(e: any) => {
                setInputNoteValue(e.target.value);
              }}
              onBlur={(e: any) => {
                updateReceiverNote(user?.id, conversation?.receiver?.id, e.target.value);
              }}
              maxLength={200}
            />
          </div>
        </SidebarCard>
        <SidebarCard title="" bordered={false}>
          {tagsComponent}
        </SidebarCard>
        <SidebarCard title="" bordered={false}>
          {selectCampaignComponent}
        </SidebarCard>
      </ul>

      <DeepResearchModal
        visible={deepResearchModalVisible}
        onCancel={() => setDeepResearchModalVisible(false)}
        onResearch={handleDeepResearch}
        email={getEmailFromConversation()}
        name={getContactNameFromConversation()}
      />

      <SaveCompanyNameModal
        visible={saveCompanyNameModalVisible}
        onCancel={() => setSaveCompanyNameModalVisible(false)}
        onSave={(companyName: string) => {
          updateClient({ company_name: companyName });
          setSaveCompanyNameModalVisible(false);
          setDeepResearchModalVisible(true);
        }}
      />

      <SalesAssistantModal
        campaign_id={conversation.campaign_id}
        user_id={user.id}
        receiver_id={conversation.receiver.id}
        isChatHistoryLoading={deepResearch.getChatMessagesLoading}
        visible={salesAssistantModalVisible}
        onCancel={() => {
          setSalesAssistantModalVisible(false);
          clearChatError();
          clearChatHistory();
        }}
        onSendMessage={handleSalesAssistantMessage}
        contactName={getContactNameFromConversation()}
        chatHistory={deepResearch.chatHistory}
        chatHistoryMetaData={deepResearch.chatHistoryMetaData}
        chatLoading={deepResearch.chatLoading}
        chatError={deepResearch.chatError}
        getChatMessages={getChatMessages}
      />

      <MarkdownViewerModal
        visible={markdownModalVisible}
        onCancel={() => {
          setMarkdownModalVisible(false);
          setSelectedFile(undefined);
        }}
        fileUrl={selectedFile?.fileUrl}
        id={selectedFile?.id}
        receiverName={conversation?.receiver?.first_name || conversation?.receiver?.email || 'unknown'}
        title={`Research Document - ${conversation?.receiver?.first_name || conversation?.receiver?.email || 'Contact'}`}
        exportDeepResearchDocument={exportDeepResearchDocument}
      />
    </div>
  );
};
